"use client";
import { useSyncExternalStore } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type RoleScope = "GLOBAL" | "DEPT" | "MEMBER" | null;

export interface AuthUser {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  email: string | null;
  deptId: string | null;
  roleId: string | null;
  roleName?: string | null;
  roleScope?: RoleScope;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  mustChangePw: boolean;
  hasAvatar: boolean;
  setSession: (s: { token: string; user: AuthUser; mustChangePw: boolean; hasAvatar: boolean }) => void;
  updateUser: (u: Partial<AuthUser>) => void;
  updateFlags: (f: { mustChangePw?: boolean; hasAvatar?: boolean }) => void;
  clear: () => void;
  _hasHydrated: boolean;
  _setHasHydrated: (b: boolean) => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      mustChangePw: false,
      hasAvatar: false,
      _hasHydrated: false,
      _setHasHydrated: (b) => set({ _hasHydrated: b }),
      setSession: ({ token, user, mustChangePw, hasAvatar }) => set({ token, user, mustChangePw, hasAvatar }),
      updateUser: (u) => set((s) => ({ user: s.user ? { ...s.user, ...u } : s.user })),
      updateFlags: (f) => set((s) => ({ mustChangePw: f.mustChangePw ?? s.mustChangePw, hasAvatar: f.hasAvatar ?? s.hasAvatar })),
      clear: () => set({ token: null, user: null, mustChangePw: false, hasAvatar: false }),
    }),
    {
      name: "taskflow-auth",
      onRehydrateStorage: () => (state) => {
        state?._setHasHydrated(true);
      },
    }
  )
);

// SSR-safe hook: 告诉调用方 store 是否已从 localStorage 恢复完比。
// 用 useSyncExternalStore 避免 useState + useEffect 跟不上首帧的问题。
export function useAuthHydrated(): boolean {
  return useSyncExternalStore(
    (cb) => useAuth.subscribe((s) => cb()),
    () => useAuth.getState()._hasHydrated,
    () => false // SSR: 总是 false
  );
}
